import React, { useState, useEffect, useCallback } from "react";
import { Search, RefreshCw, ChevronDown } from "lucide-react";
import { TrendUp } from "iconsax-reactjs";
import apiService from "../../services/api";
import { useNavigate } from "react-router-dom";
import LeadSearchInput from "../../components/LeadSearchInput";
import { useTheme } from "../../contexts/ThemeContext";

const CleansedLeads = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [basicFilters, setBasicFilters] = useState({
    status: "",
    search: "",
  });

  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchLeads();
    fetchTeamMembers();
  }, [pagination.page, basicFilters]);

  const fetchTeamMembers = async () => {
    try {
      const teamsResponse = await apiService.getUserTeams();
      const teams = teamsResponse.teams || [];
      if (teams.length === 0) return;

      const allMembers = new Map();
      for (const team of teams) {
        try {
          const membersResponse = await apiService.getTeamMembers(team.id);
          const members = membersResponse.members || [];
          members.forEach((member) => {
            if (member.user && member.status === "active") {
              allMembers.set(member.user.id, {
                id: member.user.id,
                name: member.user.name,
                email: member.user.email,
                profileImageUrl: member.user.profileImageUrl,
                role: member.user.role,
              });
            }
          });
        } catch (err) {
          console.error(`Error fetching members for team ${team.id}:`, err);
        }
      }
      setUsers(Array.from(allMembers.values()));
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCleansedLeads({
        page: pagination.page,
        limit: pagination.limit,
        ...basicFilters,
      });
      setLeads(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Error fetching cleansed leads:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (window.confirm("Are you sure you want to delete this cleansed lead?")) {
      try {
        await apiService.deleteCleansedLead(leadId);
        fetchLeads();
      } catch (error) {
        console.error("Error deleting cleansed lead:", error);
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: `text-blue-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`,
      nurture: `text-orange-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`,
      qualified: `text-green-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`,
      unqualified: `text-gray-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`,
      converted: `text-purple-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`,
      lost: `text-red-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`,
    };
    return (
      colors[status] ||
      `text-gray-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`
    );
  };

  const getStatusLabel = (status) => {
    const labels = {
      unqualified: "Unqualified",
      lead_pot: "Lead Pot",
      new: "New",
      nurture: "Nurture",
      dead: "Dead",
      converted: "Converted",
    };
    return labels[status] || "Unknown";
  };

  const LeadRow = ({ lead }) => {
    const initials = lead.fullName
      ? lead.fullName
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2)
      : "??";

    const assignedUser = users.find((u) => u.id === lead.assignedTo);

    return (
      <tr
        className={`border-b transition-colors ${isDarkMode ? "border-gray-700 hover:bg-gray-800/50" : "border-gray-200 hover:bg-gray-50"}`}
      >
        <td className="py-4 px-4">
          <button
            onClick={() => window.open(`/cleansed-leads/${lead.id}`, "_blank")}
            className={`font-medium hover:text-blue-400 transition-colors ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {lead.companyName || "N/A"}
          </button>
        </td>
        <td className="py-4 px-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
            >
              {initials}
            </div>
            <div>
              <div
                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {lead.fullName || "N/A"}
              </div>
              <div
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {lead.email || "N/A"}
              </div>
            </div>
          </div>
        </td>
        <td className="py-4 px-4">
          {lead.phone ? (
            <a
              href={`tel:${lead.phone}`}
              className="text-blue-400 hover:text-blue-300 transition-colors hover:underline"
            >
              {lead.phone}
            </a>
          ) : (
            <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              N/A
            </span>
          )}
        </td>
        <td className="py-4 px-4">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full border text-xs font-medium ${isDarkMode ? "border-[#373A41]" : "border-gray-300"} ${getStatusColor(lead.leadStatus)}`}
          >
            <div className="w-2 h-2 rounded-full bg-current mr-2"></div>
            {getStatusLabel(lead.leadStatus)}
          </span>
        </td>
        <td className="py-4 px-4">
          {assignedUser ? (
            <div className="flex items-center gap-2">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
              >
                {assignedUser.profileImageUrl ? (
                  <img
                    src={assignedUser.profileImageUrl}
                    alt={assignedUser.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  assignedUser.name?.charAt(0).toUpperCase()
                )}
              </div>
              <span
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {assignedUser.name}
              </span>
            </div>
          ) : (
            <span
              className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              Unassigned
            </span>
          )}
        </td>
        <td className="py-4 px-4 text-right">
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() =>
                window.open(`/cleansed-leads/${lead.id}`, "_blank")
              }
              className={`transition-colors p-1 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-700"}`}
            >
              <Search size={16} />
            </button>
            <button
              onClick={() => handleDeleteLead(lead.id)}
              className="text-gray-400 hover:text-red-400 transition-colors p-1"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path d="M6 2h4v1H6V2zm7 1h-2V2a1 1 0 00-1-1H6a1 1 0 00-1 1v1H3a1 1 0 000 2h1v8a2 2 0 002 2h4a2 2 0 002-2V5h1a1 1 0 100-2zM6 13V6h1v7H6zm3 0V6h1v7H9z" />
              </svg>
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div
      className={`min-h-screen font-inter ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-gray-50 text-gray-900"}`}
    >
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1
              className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Cleansed Leads
            </h1>
            <p
              className={`text-sm sm:text-base leading-relaxed ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Verified and cleansed leads ready for follow-up.
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            <button
              onClick={fetchLeads}
              className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg transition-colors text-sm sm:text-base font-medium min-w-0 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
            >
              <RefreshCw size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Refresh</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <LeadSearchInput
              value={basicFilters.search}
              onChange={(value) =>
                setBasicFilters({ ...basicFilters, search: value })
              }
              placeholder="Search cleansed leads..."
            />
          </div>
        </div>

        {/* Table */}
        <div
          className={`rounded-lg border mb-8 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"}`}
        >
          <div
            className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div>
              <h2
                className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Cleansed Leads{" "}
                <span
                  className={`text-sm ml-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {pagination.total} leads
                </span>
              </h2>
              <p
                className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Leads that have been verified and cleansed.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className={isDarkMode ? "bg-[#13161B]" : "bg-gray-50"}>
                <tr
                  className={`border-b ${isDarkMode ? "border-[#22262F]" : "border-gray-200"}`}
                >
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Company
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Contact
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Phone Number
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Status
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Assigned To
                  </th>
                  <th
                    className={`text-right py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="6"
                      className={`py-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Loading cleansed leads...
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className={`py-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      No cleansed leads found. Move leads from the Leads page to
                      get started.
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => <LeadRow key={lead.id} lead={lead} />)
                )}
              </tbody>
            </table>
          </div>

          <div
            className={`flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t gap-4 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page - 1 })
                }
                disabled={pagination.page === 1}
                className={`px-3 py-1 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page + 1 })
                }
                disabled={pagination.page >= pagination.totalPages}
                className={`px-3 py-1 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
              >
                Next
              </button>
            </div>
            <span
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Page {pagination.page} of {pagination.totalPages}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CleansedLeads;
